import { Box, Divider, Stack } from '@mui/material';
import { useEffect, useState } from 'react';

import CopyableCode from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupOwnCloudState } from './SetupOwnCloudState';
import { BulletItem } from './bulletItem';
import { extractErrorMessage } from './errorHandling';
import { FormTextField } from './formTextField';
import { NumericBulletSection } from './numericBulletSection';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';

export const ConnectAppRegistrations = (
  props: StepProps<SetupOwnCloudState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dal = useDAL();
  const { data: azureBackupApplications } = dal.onboarding.applications.get();

  useEffect(() => {
    if (azureBackupApplications) {
      const updates: Partial<SetupOwnCloudState> = {};
      // Set management application ID if available
      if (azureBackupApplications.managementAppId) {
        updates.managementApplicationId =
          azureBackupApplications.managementAppId;
      }
      // Set customer tenant ID if available
      if (azureBackupApplications.customerTenantId) {
        updates.tenantId = azureBackupApplications.customerTenantId;
      }
      // Set key vault application ID if available
      if (azureBackupApplications.keyVaultAccessAppId) {
        updates.keyVaultApplicationId =
          azureBackupApplications.keyVaultAccessAppId;
      }
      // Set backup app ID if available
      if (azureBackupApplications.backupAppId) {
        updates.backupAppId = azureBackupApplications.backupAppId;
      }
      // Set default application names if not already set
      if (!props.wizardState.managementApplicationName) {
        updates.managementApplicationName = 'eon-management-app';
      }
      if (!props.wizardState.backupAppName) {
        updates.backupAppName = 'eon-backup-app';
      }
      if (Object.keys(updates).length > 0) {
        props.setWizardState({
          ...props.wizardState,
          ...updates,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureBackupApplications]);

  const code = `\
module "azure_byo_backup_apps" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/byo_backup_apps.zip"

  management_app_name             = "${props.wizardState.managementApplicationName ?? ''}"
  backup_app_name                 = "${props.wizardState.backupAppName ?? ''}"
  keyvault_access_app_id          = "${props.wizardState.keyVaultApplicationId ?? ''}"
  key_vault_resource_id           = "${props.wizardState.keyVaultId ?? ''}"
  management_app_secret_name      = "${props.wizardState.keyVaultSecretName ?? ''}"
} `;

  const validateInput = () => {
    // GUID regex pattern
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Validate fields from the second section
    const hasSecondSectionFields =
      !!props.wizardState.keyVaultAccessServicePrincipalId &&
      !!props.wizardState.managementApplicationId &&
      !!props.wizardState.managementApplicationServicePrincipalId &&
      !!props.wizardState.backupAppId &&
      !!props.wizardState.backupAppServicePrincipalId &&
      !!props.wizardState.KeyVaultSecretUrl;

    // Validate GUID format for all service principal IDs and app IDs
    const hasValidGuids =
      guidRegex.test(
        props.wizardState.keyVaultAccessServicePrincipalId || ''
      ) &&
      guidRegex.test(props.wizardState.managementApplicationId || '') &&
      guidRegex.test(
        props.wizardState.managementApplicationServicePrincipalId || ''
      ) &&
      guidRegex.test(props.wizardState.backupAppId || '') &&
      guidRegex.test(props.wizardState.backupAppServicePrincipalId || '');

    return hasSecondSectionFields && hasValidGuids;
  };

  const onNextClick = async () => {
    if (!validateInput()) {
      console.log('Invalid input - check the fields and try again');
      setError(new Error('Invalid input - check the fields and try again'));
      return;
    }

    try {
      setError(null);
      setIsDeploying(true);
      await dal.onboarding.applications.byo.deploy({
        customerTenantId: props.wizardState.tenantId!,
        keyVaultAccessServicePrincipalId:
          props.wizardState.keyVaultAccessServicePrincipalId!,
        managementAppId: props.wizardState.managementApplicationId!,
        managementServicePrincipalId:
          props.wizardState.managementApplicationServicePrincipalId!,
        backupAppId: props.wizardState.backupAppId!,
        backupServicePrincipalId:
          props.wizardState.backupAppServicePrincipalId!,
        secretUrl: props.wizardState.KeyVaultSecretUrl!,
      });
      props.setNextStep(props.currentStep.next?.[0]);
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={0}
        stepperLabels={props.stepperLabels}
        onBackClick={props.back}
        canGoNext={() =>
          !isDeploying &&
          !!props.wizardState.managementApplicationName &&
          !!props.wizardState.managementApplicationServicePrincipalId &&
          !!props.wizardState.backupAppId &&
          !!props.wizardState.backupAppServicePrincipalId
        }
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
          <Stack
            gap='40px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            <NumericBulletSection
              bulletNumber={1}
              title='Deploy Eon’s management access'
            >
              <BulletItem
                title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
                leftContent={
                  <Stack gap='24px'>
                    <FormTextField
                      title='Eon management application name'
                      placeholder='Enter a name for your Eon management application'
                      value={props.wizardState.managementApplicationName || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          managementApplicationName: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='Eon backup application name'
                      placeholder='Enter a name for your Eon backup application'
                      value={props.wizardState.backupAppName || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          backupAppName: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='Key vault access application id'
                      placeholder='Enter the application id of the key vault access application'
                      value={props.wizardState.keyVaultApplicationId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          keyVaultApplicationId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='Key vault resource id'
                      placeholder='Enter the resource id of the key vault'
                      value={props.wizardState.keyVaultId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          keyVaultId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='Key vault management app secret name'
                      placeholder='Enter the name of the key vault management app secret'
                      value={props.wizardState.keyVaultSecretName || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          keyVaultSecretName: event.target.value,
                        })
                      }
                    />
                  </Stack>
                }
                rightContent={<CopyableCode title='main.tf' code={code} />}
              />
            </NumericBulletSection>
            <NumericBulletSection
              bulletNumber={2}
              title='Complete configuration'
            >
              <BulletItem
                title='Bring back the Terraform outputs to complete this step.'
                leftContent={
                  <Stack gap='24px' sx={{ mb: '24px' }}>
                    <FormTextField
                      title='keyvault_access_service_principal_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={
                        props.wizardState.keyVaultAccessServicePrincipalId || ''
                      }
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          keyVaultAccessServicePrincipalId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='management_app_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={props.wizardState.managementApplicationId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          managementApplicationId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='management_service_principal_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={
                        props.wizardState
                          .managementApplicationServicePrincipalId || ''
                      }
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          managementApplicationServicePrincipalId:
                            event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='backup_app_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={props.wizardState.backupAppId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          backupAppId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='backup_service_principal_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={
                        props.wizardState.backupAppServicePrincipalId || ''
                      }
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          backupAppServicePrincipalId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='management_app_secret_url'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={props.wizardState.KeyVaultSecretUrl || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          KeyVaultSecretUrl: event.target.value,
                        })
                      }
                    />
                  </Stack>
                }
              />
            </NumericBulletSection>
          </Stack>
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => setError(null)}
        error={error}
      />
    </>
  );
};
